import type {Command} from '~/core/config';
import type {Build, Run} from '~/commands/build';

import {createHash} from 'node:crypto';
import {join} from 'node:path';
import {extractFrontMatter} from '@diplodoc/liquid';
import {dump as yamlDump, load as yamlLoad} from 'js-yaml';
import pmap from 'p-map';

import {getHooks as getBaseHooks} from '~/core/program';
import {valuable} from '~/core/config';
import {OutputFormat} from '~/commands/build/config';

import {options} from './config';

export const CONTENT_MAP_FILENAME = 'yfm-build-content.json';
export const SCHEMA_VERSION = 1;
const HASH_CONCURRENCY = 30;

type ContentHashEntry = {
    hash: string;
    size: number;
};

type BuildContentFormat = {
    schemaVersion: typeof SCHEMA_VERSION;
    contentHashes: Record<string, ContentHashEntry>;
    pageAssets: Record<string, NormalizedPath[]>;
};

export type BuildContentMapArgs = {
    buildContent: boolean;
};

export type BuildContentMapConfig = {
    buildContent: boolean;
};

type PageAssets = Record<string, NormalizedPath[]>;

// Walks `run.entry.relations` and returns, per entry, the sorted list of its
// direct `resource`-type dependencies (images, videos, svg, etc.). Sources
// (includes) are deliberately skipped — they propagate to entry hashes
// through either `mergeIncludes` (inline content) or `hashIncludes`
// (signlink-renamed filenames in references). Resource deps are the only
// ones whose changes do NOT propagate through the entry's file content.
export function collectPageAssets(run: Run): PageAssets {
    const result: PageAssets = {};
    const relations = run.entry.relations;

    for (const node of relations.overallOrder()) {
        const data = relations.getNodeData(node) as {type?: string} | undefined;
        if (data?.type !== 'entry') {
            continue;
        }

        const assets: NormalizedPath[] = [];
        for (const dep of relations.directDependenciesOf(node)) {
            const depData = relations.getNodeData(dep) as {type?: string} | undefined;
            if (depData?.type === 'resource') {
                assets.push(dep as NormalizedPath);
            }
        }

        if (assets.length === 0) {
            continue;
        }

        assets.sort(byCodepoint);
        result[node] = assets;
    }

    return result;
}

// Matches `signlink` output naming: `name-{12hex}.{ext}` produced by
// `packages/cli/src/commands/build/features/output-md/utils.ts:94`. The
// 12-hex suffix is `sha256(content).slice(0, 12)`. Anchored to require both
// a non-empty name and a non-empty extension so we don't false-match real
// filenames that happen to contain a 12-char hex segment.
const SIGNLINK_RE = /^(.+)-([0-9a-f]{12})(\.[^.]+)$/;

// Resolves an output-path back to the stable source-path used as a key in
// `contentHashes`. Without this, two builds that differ only in include
// content would have completely different keys (`inc-abc.md` vs `inc-def.md`)
// and the consumer couldn't pair them up.
export function mapOutputToSource(outputPath: NormalizedPath, run: Run): NormalizedPath {
    const relations = run.entry.relations;

    // Identity mapping: the output filename matches a node already in the
    // graph. Covers entries, leading pages, and resources.
    if (relations.hasNode(outputPath)) {
        return outputPath;
    }

    // Try to reverse signlink: split `name-{12hex}.{ext}` and check whether
    // `name.{ext}` exists as a `source`-type node in the graph.
    const match = SIGNLINK_RE.exec(outputPath);
    if (match) {
        const [, base, , ext] = match;
        const candidate = `${base}${ext}` as NormalizedPath;
        if (relations.hasNode(candidate)) {
            const data = relations.getNodeData(candidate) as {type?: string} | undefined;
            if (data?.type === 'source') {
                return candidate;
            }
        }
    }

    // Unknown file (e.g. theme assets that aren't tracked in entry graph).
    // Identity mapping keeps the file present in `contentHashes` so the
    // consumer can still detect its changes.
    return outputPath;
}

// `Run` uses `<output>/.tmp_input/` as a scratch directory during build
// (see core/Run); those files still exist when AfterAnyRun glob's the
// output, so we filter them out explicitly to keep `contentHashes` free
// of duplicates of the real output entries.
const TMP_INPUT_PREFIX = '.tmp_input/';

// Build-artifact filenames at the output root (the artifacts the CLI
// itself emits). These would pollute `contentHashes` with self-references
// and, in some cases (crawler-manifest), with non-deterministic bytes.
const ARTIFACT_FILENAMES = new Set([
    'yfm-build-manifest.json',
    'yfm-build-stats.json',
    'yfm-build-content.json',
    'crawler-manifest.json',
    'files.json',
]);

// Matches the existing `yfm-*-meta.json` family (e.g.
// `yfm-redirects-meta-file.json`).
const META_ARTIFACT_RE = /^yfm-.+-meta.*\.json$/;

// `llms.txt` / `llms-full.txt` are emitted per-toc (so they may live in nested
// language/section dirs, not only at the output root). They are derived from
// the entries' own content and change on every edit, so they must not pollute
// `contentHashes` — match them by basename regardless of directory depth.
const LLMS_ARTIFACT_RE = /(^|\/)llms(-full)?\.txt$/;

export function isExcludedServiceFile(outputPath: NormalizedPath): boolean {
    if (outputPath.startsWith(TMP_INPUT_PREFIX)) {
        return true;
    }
    if (ARTIFACT_FILENAMES.has(outputPath)) {
        return true;
    }
    if (LLMS_ARTIFACT_RE.test(outputPath)) {
        return true;
    }
    return META_ARTIFACT_RE.test(outputPath);
}

// pmap insertion order is scheduler-dependent. To guarantee byte-level
// determinism in the emitted JSON, we rebuild each top-level dictionary
// with keys in alphabetical insertion order before serializing.
function sortKeys<V>(obj: Record<string, V>): Record<string, V> {
    const sortedKeys = Object.keys(obj).sort(byCodepoint);
    const result: Record<string, V> = {};
    for (const key of sortedKeys) {
        result[key] = obj[key];
    }
    return result;
}

// Locale-agnostic string comparator. Equivalent to the default `.sort()`
// for strings (UTF-16 codepoint order), but explicit so the intent is
// readable and platform/locale-independent for cross-machine determinism.
// `localeCompare` would defeat the purpose: different locales yield
// different orders, flipping the content-hash on different runners.
function byCodepoint(a: string, b: string): number {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
}

// Prefixing with the algorithm name lets us migrate to a different hash
// (e.g. blake3) in a schema v2 without ambiguity for the consumer.
export function hashContent(content: Buffer): string {
    return 'sha256-' + createHash('sha256').update(content).digest('hex');
}

const VOLATILE_META_KEYS = ['updatedAt', 'contributors', 'author'] as const;
// Names of items inside the `metadata: [...]` array (where each item has
// shape `{name, content}`) that are CLI build-time noise rather than
// authored content. The `generator` item is the CLI's package.json
// version baked into every entry's frontmatter — it flips on every
// release without any user-doc change.
const VOLATILE_METADATA_ITEM_NAMES = new Set(['generator']);

// Strip VCS-injected metadata (`updatedAt`, `contributors`, `author`) and
// CLI-injected `metadata.generator` from the frontmatter of .md files and
// the `meta:` block of .yaml leading pages before hashing.
//
// Why: with `mtimes: true`, `VcsService.metadata` injects an ISO timestamp
// into every entry's frontmatter. That timestamp is the max over the page
// AND its include deps, so any commit touching any included file shifts
// the parent's hash without changing what readers see. Likewise the CLI
// bakes its own version into every entry as `metadata.generator`, which
// flips on every release. We hash a normalized view so the hash reflects
// content, not build-time noise.
//
// Returns the raw input bytes unchanged when there's nothing to strip,
// so non-md/non-yaml files (and entries without volatile fields) keep
// their `hash == sha256(file bytes)` property.
export function normalizeForHash(content: Buffer, path: NormalizedPath): Buffer {
    if (path.endsWith('.md')) {
        return normalizeMarkdown(content);
    }
    if (path.endsWith('.yaml') || path.endsWith('.yml')) {
        return normalizeYaml(content);
    }
    return content;
}

function isVolatileMetadataItem(item: unknown): boolean {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return false;
    }
    const name = (item as Record<string, unknown>).name;
    return typeof name === 'string' && VOLATILE_METADATA_ITEM_NAMES.has(name);
}

function hasVolatile(obj: Record<string, unknown>): boolean {
    if (VOLATILE_META_KEYS.some((k) => k in obj)) {
        return true;
    }
    const metadata = obj.metadata;
    if (Array.isArray(metadata) && metadata.some(isVolatileMetadataItem)) {
        return true;
    }
    return false;
}

// The inline-SVG transform (md2md, see output-md/plugins/merge-svg.ts) inlines
// SVG assets and runs them through SVGO `prefixIds`, which rewrites every id
// inside the SVG to `<prefix>__<id>`. The prefix comes from the build's
// id-generator; with the default `random` strategy it is `svg-<8 base36 chars>`
// derived from `Math.random()`, so it differs on every build of an unchanged
// source. That volatile prefix would flip the page's content-hash on each
// rebuild, producing false-positive diffs for consumers (change
// notifications, search reindex). Canonicalize it to a fixed marker so the
// hash reflects the SVG's content, not its per-build ids. The same prefix
// appears in both `id="…"` definitions and their `url(#…)` / href references,
// and the global replace rewrites all of them identically, so they stay
// internally consistent. Real SVG content (paths, dimensions, the original
// id stems after `__`) is preserved, so genuine changes still move the hash.
//
// Coupled to transform's prefix shape (`@diplodoc/transform`
// plugins/images `replaceSvgContent` → SVGO `prefixIds` with prefix
// `generateID('svg')`). If transform changes that shape this stops matching;
// the `inline svg ids` tests pin the current shape.
const VOLATILE_SVG_ID_PREFIX_RE = /svg-[0-9a-z]+__/g;

function canonicalizeSvgIds(text: string): string {
    return text.replace(VOLATILE_SVG_ID_PREFIX_RE, 'svg__');
}

function normalizeMarkdown(content: Buffer): Buffer {
    // Canonicalize volatile inline-SVG ids before any frontmatter handling so
    // every return path below hashes the de-noised body. When the source has
    // no inlined SVG this is a no-op and `text === content`'s utf8 view, so
    // files without SVG ids keep their `hash == sha256(file bytes)` property.
    const text = canonicalizeSvgIds(content.toString('utf8'));

    let fm: unknown;
    let body: string;
    let rawFrontmatter: string;
    try {
        const [parsed, stripped, raw] = extractFrontMatter(text);
        fm = parsed;
        body = stripped;
        rawFrontmatter = raw;
    } catch {
        // Malformed YAML inside the fences — hash the SVG-canonicalized bytes.
        return Buffer.from(text, 'utf8');
    }

    // `extractFrontMatter` returns an empty string for `rawFrontmatter`
    // when the file has no frontmatter fences at all (or when fences are
    // unbalanced and don't match its regex). In both cases there's no
    // frontmatter to strip — hash the SVG-canonicalized bytes.
    if (!rawFrontmatter) {
        return Buffer.from(text, 'utf8');
    }

    if (!fm || typeof fm !== 'object' || Array.isArray(fm)) {
        return Buffer.from(text, 'utf8');
    }
    const fmObj = fm as Record<string, unknown>;

    if (!hasVolatile(fmObj)) {
        return Buffer.from(text, 'utf8');
    }

    const strippedFm = stripVolatile(fmObj);
    if (Object.keys(strippedFm).length === 0) {
        return Buffer.from(body, 'utf8');
    }
    const newFm = yamlDump(strippedFm, {sortKeys: true});
    return Buffer.from('---\n' + newFm + '---\n' + body, 'utf8');
}

function normalizeYaml(content: Buffer): Buffer {
    const text = content.toString('utf8');
    let parsed: unknown;
    try {
        parsed = yamlLoad(text);
    } catch {
        return content;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return content;
    }
    const doc = parsed as Record<string, unknown>;

    const meta = doc.meta;
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
        return content;
    }
    const metaObj = meta as Record<string, unknown>;
    if (!hasVolatile(metaObj)) {
        return content;
    }

    const stripped = stripVolatile(metaObj);
    if (Object.keys(stripped).length === 0) {
        delete doc.meta;
    } else {
        doc.meta = stripped;
    }
    return Buffer.from(yamlDump(doc, {sortKeys: true}), 'utf8');
}

function stripVolatile(obj: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
        if ((VOLATILE_META_KEYS as readonly string[]).includes(key)) {
            continue;
        }
        if (key === 'metadata' && Array.isArray(obj[key])) {
            const items = (obj[key] as unknown[]).filter((i) => !isVolatileMetadataItem(i));
            if (items.length > 0) {
                out[key] = items;
            }
            continue;
        }
        out[key] = obj[key];
    }
    return out;
}

export class BuildContentMap {
    apply(program: Build) {
        getBaseHooks(program).Command.tap('BuildContentMap', (command: Command) => {
            command.addOption(options.buildContent);
        });

        getBaseHooks(program).Config.tapPromise('BuildContentMap', async (config, args) => {
            // Default to on for md2md builds: the content map is consumed by
            // post-build tooling (search reindex, change notifications) that
            // diffs the md output uploaded from this format. For html builds
            // it stays off — there's no comparable consumer there yet. Users
            // can flip either side via `--no-build-content` / `buildContent: false`.
            let buildContent = config.outputFormat === OutputFormat.md;

            if (valuable(config.buildContent)) {
                buildContent = Boolean(config.buildContent);
            }

            if (valuable(args.buildContent)) {
                buildContent = Boolean(args.buildContent);
            }

            config.buildContent = buildContent;

            return config;
        });

        getBaseHooks<Run>(program).AfterAnyRun.tapPromise('BuildContentMap', async (run) => {
            if (!run.config.buildContent) {
                return;
            }

            const pageAssets = sortKeys(collectPageAssets(run));
            const contentHashes = sortKeys(await hashOutput(run));

            const manifest: BuildContentFormat = {
                schemaVersion: SCHEMA_VERSION,
                contentHashes,
                pageAssets,
            };

            await run.write(
                join(run.output, CONTENT_MAP_FILENAME),
                JSON.stringify(manifest, null, 2),
                true,
            );
        });
    }
}

// Walks the output directory, filters out service files, and concurrently
// reads + hashes each remaining file. Keys are source-paths so two builds
// with different signlink suffixes still pair up on the consumer side.
async function hashOutput(run: Run): Promise<Record<string, ContentHashEntry>> {
    let files: NormalizedPath[];
    try {
        files = await run.glob('**/*', {cwd: run.output});
    } catch (error) {
        run.logger.warn(`BuildContentMap: failed to glob output directory: ${error}`);
        return {};
    }

    const eligible = files.filter((file) => !isExcludedServiceFile(file));
    const result: Record<string, ContentHashEntry> = {};

    // pmap with bounded concurrency — without this we'd open every output
    // file at once and trip EMFILE on large docs.
    await pmap(
        eligible,
        async (file) => {
            const abs = join(run.output, file);
            try {
                // Project's `globals.d.ts` narrows `readFile` to require an
                // encoding option; cast through unknown to call the underlying
                // Node API with no options, which returns a Buffer.
                const readFile = run.fs.readFile as unknown as (
                    path: AbsolutePath,
                ) => Promise<Buffer>;
                const content = await readFile(abs);
                const stat = await run.fs.stat(abs);
                const source = mapOutputToSource(file, run);
                result[source] = {
                    hash: hashContent(normalizeForHash(content, file)),
                    size: stat.size,
                };
            } catch (error) {
                // File may have been removed between glob and read, or
                // permissions denied — log and skip rather than abort the
                // whole build artifact.
                run.logger.warn(`BuildContentMap: failed to hash ${file}: ${error}`);
            }
        },
        {concurrency: HASH_CONCURRENCY},
    );

    return result;
}
